import { create } from 'zustand';
import { Guide } from '../types/guide';
import { collection, doc, setDoc, deleteDoc, query, where, onSnapshot, updateDoc, writeBatch, getDocs, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { deleteUserAuth } from '../lib/firebase/auth';

type GuidesState = {
  guides: Record<string, Guide>;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  initializeListener: () => () => void;
  createGuide: (guide: Guide) => Promise<void>;
  updateGuide: (guideId: string, updates: Partial<Guide>) => Promise<void>;
  deleteGuide: (guideId: string) => Promise<void>;
  resetPassword: (guideId: string, newPassword: string) => Promise<void>;
};

export const useGuidesStore = create<GuidesState>((set, get) => ({
  guides: {},
  loading: false,
  error: null,
  initialized: false,

  initializeListener: () => {
    if (get().initialized) return () => {};
    
    const userRole = localStorage.getItem('userRole');
    const userId = localStorage.getItem('userId');
    
    let q;
    if (userRole === 'organizer' && userId) {
      q = query(
        collection(firestore, 'Guides'),
        where('organizerId', '==', userId)
      );
    } else {
      q = collection(firestore, 'Guides');
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const guides: Record<string, Guide> = {};
      snapshot.forEach((doc) => {
        guides[doc.id] = {
          ...doc.data(),
          id: doc.id
        } as Guide;
      });
      set({ guides, initialized: true });
    }, (error) => {
      console.error('Error in guides listener:', error);
      set({ error: error.message, initialized: false });
    });

    return unsubscribe;
  },

  createGuide: async (guide: Guide) => {
    set({ loading: true, error: null });
    try {
      await setDoc(doc(firestore, 'Guides', guide.id), {
        ...guide,
        createdAt: Date.now(),
        updatedAt: Date.now()
      });
      set((state) => ({
        guides: {
          ...state.guides,
          [guide.id]: guide
        },
        loading: false
      }));
    } catch (error: any) {
      console.error('Error creating guide:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateGuide: async (guideId: string, updates: Partial<Guide>) => {
    set({ loading: true, error: null });
    try {
      const guideRef = doc(firestore, 'Guides', guideId);
      
      const updatedData = {
        ...updates,
        updatedAt: Date.now()
      };

      await updateDoc(guideRef, updatedData);

      set((state) => ({
        guides: {
          ...state.guides,
          [guideId]: {
            ...state.guides[guideId],
            ...updatedData
          }
        },
        loading: false
      }));
    } catch (error: any) {
      console.error('Error updating guide:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteGuide: async (guideId: string) => {
    set({ loading: true, error: null });
    try {
      // Get guide data
      const guideRef = doc(firestore, 'Guides', guideId);
      const guideSnap = await getDoc(guideRef);

      if (!guideSnap.exists()) {
        throw new Error('المرشد غير موجود في قاعدة البيانات');
      }

      const guideData = guideSnap.data() as Guide;
      console.log('Guide data found:', { id: guideId, ...guideData });

      // Start batch operation for Firestore updates
      const batch = writeBatch(firestore);

      // Try to delete auth user if credentials are available
      if (guideData.email && guideData.password) {
        try {
          await deleteUserAuth(guideData.email, guideData.password);
          console.log('Successfully deleted auth user for guide:', guideId);
        } catch (authError) {
          // Continue with database cleanup even if auth deletion fails
          console.warn('Failed to delete auth user, continuing with database cleanup:', authError);
        }
      } else {
        console.warn('Guide missing email or password, skipping auth deletion');
      }

      // Process related collections in batches
      await processLinksForDeletion(guideId);
      await processDisputesForDeletion(guideId);
      await processOwnershipForDeletion(guideId);
      await processGuideBansForDeletion(guideId);

      // Delete guide document
      batch.delete(guideRef);
      await batch.commit();

      // Update local state
      set(state => {
        const newGuides = { ...state.guides };
        delete newGuides[guideId];
        return { guides: newGuides, loading: false };
      });
    } catch (error: any) {
      console.error('Error deleting guide:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  resetPassword: async (guideId: string, newPassword: string) => {
    set({ loading: true, error: null });
    try {
      const guideRef = doc(firestore, 'Guides', guideId);
      
      await updateDoc(guideRef, {
        password: newPassword,
        updatedAt: Date.now()
      });

      set((state) => ({
        guides: {
          ...state.guides,
          [guideId]: {
            ...state.guides[guideId],
            password: newPassword,
            updatedAt: Date.now()
          }
        },
        loading: false
      }));
    } catch (error: any) {
      console.error('Error resetting password:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  }
}));

// Helper functions for batch operations
async function processLinksForDeletion(guideId: string) {
  const linksRef = collection(firestore, 'PilgrimGuideLinks');
  const linksQuery = query(linksRef, where('guideId', '==', guideId));
  
  // Process in batches
  let lastBatch = writeBatch(firestore);
  let operationCount = 0;
  let totalDeleted = 0;
  
  const linksSnapshot = await getDocs(linksQuery);
  console.log(`Found ${linksSnapshot.size} links to delete for guide:`, guideId);
  
  for (const doc of linksSnapshot.docs) {
    lastBatch.delete(doc.ref);
    operationCount++;
    totalDeleted++;
    
    // Firebase batches are limited to 500 operations
    if (operationCount >= 450) {
      await lastBatch.commit();
      console.log(`Committed batch of ${operationCount} link deletions`);
      lastBatch = writeBatch(firestore);
      operationCount = 0;
    }
  }
  
  if (operationCount > 0) {
    await lastBatch.commit();
    console.log(`Committed final batch of ${operationCount} link deletions`);
  }
  
  console.log(`Completed deletion of ${totalDeleted} links`);
  return totalDeleted;
}

async function processDisputesForDeletion(guideId: string) {
  // Find disputes where this guide is involved
  const disputesRef = collection(firestore, 'Disputes');
  const disputesSnapshot = await getDocs(disputesRef);
  
  let lastBatch = writeBatch(firestore);
  let operationCount = 0;
  let totalDeleted = 0;
  
  // We need to check each dispute manually since Firebase doesn't support direct querying of array elements
  for (const docSnapshot of disputesSnapshot.docs) {
    const disputeData = docSnapshot.data();
    const disputingGuides = disputeData.disputingGuides || [];
    
    if (disputingGuides.some((g: any) => g.guideId === guideId)) {
      lastBatch.delete(docSnapshot.ref);
      operationCount++;
      totalDeleted++;
      
      if (operationCount >= 450) {
        await lastBatch.commit();
        console.log(`Committed batch of ${operationCount} dispute deletions`);
        lastBatch = writeBatch(firestore);
        operationCount = 0;
      }
    }
  }
  
  if (operationCount > 0) {
    await lastBatch.commit();
    console.log(`Committed final batch of ${operationCount} dispute deletions`);
  }
  
  console.log(`Completed deletion of ${totalDeleted} disputes`);
  return totalDeleted;
}

async function processOwnershipForDeletion(guideId: string) {
  // Find ownership records related to this guide
  const ownershipRef = collection(firestore, 'PilgrimOwnership');
  const ownershipQuery = query(ownershipRef, where('ownerGuideId', '==', guideId));
  
  let lastBatch = writeBatch(firestore);
  let operationCount = 0;
  let totalDeleted = 0;
  
  const ownershipSnapshot = await getDocs(ownershipQuery);
  console.log(`Found ${ownershipSnapshot.size} ownership records to delete for guide:`, guideId);
  
  for (const doc of ownershipSnapshot.docs) {
    lastBatch.delete(doc.ref);
    operationCount++;
    totalDeleted++;
    
    if (operationCount >= 450) {
      await lastBatch.commit();
      console.log(`Committed batch of ${operationCount} ownership record deletions`);
      lastBatch = writeBatch(firestore);
      operationCount = 0;
    }
  }
  
  // Also check for realGuideId references
  const realGuideQuery = query(ownershipRef, where('realGuideId', '==', guideId));
  const realGuideSnapshot = await getDocs(realGuideQuery);
  
  for (const doc of realGuideSnapshot.docs) {
    // If this is not already deleted (could be the same as ownerGuideId)
    if (!ownershipSnapshot.docs.some(d => d.id === doc.id)) {
      lastBatch.delete(doc.ref);
      operationCount++;
      totalDeleted++;
      
      if (operationCount >= 450) {
        await lastBatch.commit();
        console.log(`Committed batch of ${operationCount} ownership record deletions`);
        lastBatch = writeBatch(firestore);
        operationCount = 0;
      }
    }
  }
  
  if (operationCount > 0) {
    await lastBatch.commit();
    console.log(`Committed final batch of ${operationCount} ownership record deletions`);
  }
  
  console.log(`Completed deletion of ${totalDeleted} ownership records`);
  return totalDeleted;
}

async function processGuideBansForDeletion(guideId: string) {
  // Find guide ban records
  const bansRef = collection(firestore, 'GuideBans');
  const bansQuery = query(bansRef, where('guideId', '==', guideId));
  
  let lastBatch = writeBatch(firestore);
  let operationCount = 0;
  let totalDeleted = 0;
  
  const bansSnapshot = await getDocs(bansQuery);
  console.log(`Found ${bansSnapshot.size} ban records to delete for guide:`, guideId);
  
  for (const doc of bansSnapshot.docs) {
    lastBatch.delete(doc.ref);
    operationCount++;
    totalDeleted++;
    
    if (operationCount >= 450) {
      await lastBatch.commit();
      console.log(`Committed batch of ${operationCount} ban record deletions`);
      lastBatch = writeBatch(firestore);
      operationCount = 0;
    }
  }
  
  if (operationCount > 0) {
    await lastBatch.commit();
    console.log(`Committed final batch of ${operationCount} ban record deletions`);
  }
  
  console.log(`Completed deletion of ${totalDeleted} ban records`);
  return totalDeleted;
}